/**
 * @namespace Stores
 * @category Stores
 * @module blackSpot
 * */

import api from '@/api'
import {
  createStateFlagsManager,
  createPromiseQueueManager,
} from '@/utils/loader'
import loader from '@/plugins/loader.js'
import { apiCacheStorage } from '@/api/api-cache.js'

const stateFlags = createStateFlagsManager()

const promiseQueueManager = createPromiseQueueManager(stateFlags)

const createState = () => ({
  currentItem: null, //Edition
  validityPeriods: [],
  constraints: [],
  categories: [],
  speedLimits: [],
  blackSpots: [],
  blackSpotSegmentPolylines: [], //A segment is made by multiple consecutive polylines
  blackspotLayerChangeTimestamp: null,
  stateFlagUpdateAt: null,
})

const store = (window._bss = {
  namespaced: true,
  state: createState(),
  getters: {
    validityPeriodsForBlackspotId: (state) => (blackSpotId) =>
      state.validityPeriods.filter((vp) => vp.blackSpotId === blackSpotId),
    constraints: (state) => state.constraints,
    blackSpots: (state) => {
      const constraints = state.constraints
      const speedLimits = state.speedLimits
      return state.blackSpots.map((item) => {
        let constraintItem = constraints.find((c) => c.id === item.constraintId)
        return {
          ...item,

          //Fallback solution if APIV3 do not return refs name/values
          constraintName: item.constraintName || constraintItem?.label || '',

          constraintImage: constraintItem?.image || '',

          categoryName:
            item.categoryName ||
            state.categories.find((c) => c.id === item.categoryId)?.label ||
            '',
          speedLimitSpeed:
            item.speedLimitSpeed ||
            speedLimits.find((c) => c.id === item.speedLimitId)?.speed ||
            '',
          validityPeriods: state.validityPeriods.filter(
            (vp) => vp.blackSpotId === item.id
          ),
        }
      })
    },
    isLoading: (state) => {
      return (
        !!state.stateFlagUpdateAt &&
        stateFlags.has(['loadData', 'loadBlackSpots_resolving'])
      )
    },
    blackSpotsLoaded: (state) =>
      !!state.stateFlagUpdateAt && stateFlags.has('blackSpotsLoaded'),
  },
  mutations: {},
  actions: {
    async loadData({ dispatch, rootState }) {
      let loadingFlag = stateFlags.add('loadData')
      loader.showAlternative()

      const promiseAll = Promise.all([
        dispatch('loadConstraints'),
        dispatch('loadCategories'),
        dispatch('loadSpeedLimits'),
        dispatch('loadValidityPeriods'),
      ])

      await promiseAll

      //Only load on blackspot list/table/tableMap
      const shouldLoadBlackSpots = !['nstra', 'ate', 'port'].some((str) =>
        rootState.router.name.toLowerCase().includes(str)
      )
      console.log({
        routeName: rootState.router.name,
        shouldLoadBlackSpots,
      })
      if (shouldLoadBlackSpots) {
        await dispatch('loadBlackSpots') //Wait for ref data
      }

      loader.hideAlternative()
      loadingFlag.clear()
    },
    async loadValidityPeriods({ state }) {
      const res = await api.blackSpotValidityPeriod.getAllPooling()
      state.validityPeriods = [...Object.freeze(res)]
      console.log('loadValidityPeriods')
    },
    async loadConstraints({ state }) {
      const res = await api.blackSpotConstraint.getAllPooling()
      state.constraints = [...Object.freeze(res)]
      console.log('loadConstraints')
    },
    async loadCategories({ state }) {
      //console.log('loadCategories')
      const res = await api.blackSpotCategory.getAllPooling()
      state.categories = [...Object.freeze(res)]
    },
    async loadSpeedLimits({ state }, options = {}) {
      console.log('loadSpeedLimits')

      if (
        options.lazyOnce &&
        promiseQueueManager.isQueuedOrResolved('loadSpeedLimits', true)
      ) {
        return
      }

      const res = await promiseQueueManager.queueResolve(
        'loadSpeedLimits',
        api.blackSpotSpeedLimit.getAllPooling()
      )
      state.speedLimits = [...Object.freeze(res)]
    },
    /**
     * @todo Add API cache and bypass using a param (e.g For manual refresh buttons)
     * @param {Boolean} lazyOnce Lazy load once will wait existing load operations if
     */
    async loadBlackSpots({ state }, options = {}) {
      console.log('loadBlackSpots')

      if (
        options.lazyOnce &&
        promiseQueueManager.isQueuedOrResolved('loadBlackSpots', true)
      ) {
        return
      }

      //If background mode, keep existing data available during fetch time.
      if (options.backgroundMode !== true) {
        state.blackSpots = []
      }

      let poolingPromise = api.blackSpot.getAllPooling({
        payload: {
          groups: ['populate'],
        },
      })

      let res

      if (options.bypassCache === true) {
        await apiCacheStorage.invalidateCacheByKeyInclude(
          'black_spot_black_spots'
        )
        res = await poolingPromise
      } else {
        res = await promiseQueueManager.queueResolve(
          'loadBlackSpots',
          poolingPromise
        )
      }

      state.blackSpots = [
        ...Object.freeze(res.sort((a, b) => (a.id < b.id ? 1 : -1))),
      ]
    },
  },
})

stateFlags.bindStore(store, 'stateFlagUpdateAt')

export default store
